// Promise polyfill
import 'core-js/features/promise'

import jwtDecode, { JwtPayload } from 'jwt-decode'
import { createRoot } from 'react-dom/client'
import { useEffect, useState } from 'react'
import App from './components/App'
import '@amzn/awsui-global-styles/polaris.css'
import { getMidwayJwtToken } from './auth/MidwayJwtToken'
import axios, { AxiosInstance } from 'axios'
import { StatusCodes } from 'http-status-codes'
import { AppContext, UserProps } from './context'
import { getPermissions, ROLES, userProgram } from './role'
import { SELECT_BUSINESS_ENTITY } from './components/Constant'
import CacheBuster from 'react-cache-buster'
import { BASE_URL, EGRET_URL } from './constants'
import { setResolvedStage } from './components/common/Util'

const appVersion = require('../package.json').version
const cathodeVersions = require('@amzn/cathode-versions-javascript')

interface AppBootstrapProps {
    apiClient: AxiosInstance
    userAlias: string | undefined
    egretUrl: string
    stage: string
    initialBusinessEntity: any
    userRolesPromise: Promise<any>
    isAdminPromise: Promise<any>
}

const AppBootstrap = (props: AppBootstrapProps) => {
    const {
        apiClient,
        userAlias,
        egretUrl,
        stage,
        initialBusinessEntity,
        userRolesPromise,
        isAdminPromise,
    } = props

    const [userProps, setUserProps] = useState<UserProps>({
        userAlias,
        role: [ROLES.MANAGER],
        userPrograms: [],
        permissions: getPermissions[ROLES.MANAGER],
    })

    useEffect(() => {
        Promise.all([userRolesPromise, isAdminPromise]).then(([rolesRes, isAdminRes]) => {
            const roles: ROLES[] = [ROLES.MANAGER]
            if (isAdminRes?.data?.isAdmin) {
                roles.push(ROLES.ADMIN)
            }
            const userRoleData = rolesRes?.data || {}
            let userPrograms: userProgram[] = []
            if (initialBusinessEntity.id && initialBusinessEntity.id in userRoleData) {
                const userRoleObject = userRoleData[initialBusinessEntity.id]
                const isSTL = userRoleObject['ROLES'].find((r: any) => r.id === ROLES.STL)
                userPrograms = userRoleObject['USER_PROGRAMS']
                if (isSTL) {
                    roles.push(ROLES.STL)
                }
            }
            const permissions: number[] = roles.flatMap((role) => getPermissions[role] ?? [])
            setUserProps({
                userAlias,
                role: roles,
                userPrograms,
                permissions,
            })
        })
    }, [])

    return (
        <AppContext.Provider value={{ apiClient, userAlias, egretUrl, userProps, stage }}>
            <App userBusinessEntity={initialBusinessEntity} />
        </AppContext.Provider>
    )
}

;(async () => {
    const token = await getMidwayJwtToken()
    const decoded = jwtDecode<JwtPayload>(token)
    const userAlias = decoded.sub
    const appSettings = (await axios('/settings.json')).data
    const stage = appSettings['stage']
    setResolvedStage(stage)
    const apiUrl = BASE_URL[stage]
    const egretUrl = EGRET_URL[stage]

    const apiClient = axios.create({
        baseURL: apiUrl,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        responseType: 'json',
        timeout: 29_000,
    })

    // Add Authorization header to all axios calls
    apiClient.interceptors.request.use(async (config) => {
        const jwtToken = await getMidwayJwtToken()
        config.headers.Authorization = `Bearer ${jwtToken}`
        return config
    })

    apiClient.interceptors.response.use(
        function (response) {
            if (response.status === StatusCodes.UNAUTHORIZED) {
                console.error('You are not authorized to perform the operation')
            }
            return response
        },
        function (error) {
            return Promise.reject(error)
        },
    )

    // Kick off /user-roles in parallel with /user/business-entity. /user-roles is
    // the slow endpoint (~5s on prod); the React tree renders as soon as
    // business-entity returns and the role-gated UI flips on once this resolves.
    const userRolesPromise = apiClient.get(`/user-roles/${userAlias}`).catch((err) => {
        console.error(err)
        return null
    })

    const businessEntityResponse = await apiClient.get('/user/business-entity')
    const user = businessEntityResponse.data

    const userBusinessEntity = {
        id: user?.business_entity_id ?? '',
        name: user?.business_entity_name ?? SELECT_BUSINESS_ENTITY,
        slack_channels: user?.slack_channels ?? [{ slack_channel_id: '', slack_channel_name: '' }],
        slack_message_templates: user?.slack_message_templates ?? [],
        configurations: user?.configurations ?? { project: { non_admin_creation: false } },
    }

    // /user/is-admin needs admin_team_id from /user/business-entity, so it can
    // only fire after that returns — but it runs alongside the still-pending
    // /user-roles call and is also non-blocking for the initial render.
    const isAdminPromise = user
        ? apiClient
              .get(`/user/is-admin?access_control_team_id=${user.admin_team_id}`)
              .catch((err) => {
                  console.error(err)
                  return null
              })
        : Promise.resolve(null)

    const isProduction = process.env.NODE_ENV === 'production'

    //User analytics integration(Spectrometer)
    const cathodeConfig = {
        siteName: 'Falcon',
        appName: 'FalconFrontendWebsite',
    }

    if (userAlias) {
        // Using logged in user as an unique id as cathode-customerId
        document.dispatchEvent(
            new CustomEvent('cathode-customerId', {
                detail: {
                    customerId: userAlias,
                },
            }),
        )
    }

    const scriptTags = cathodeVersions.getSpectrometerScriptTags(cathodeConfig, stage === 'prod')

    const rawBoomerangScript = scriptTags.boomerangLoaderScript

    const boomerangScript = rawBoomerangScript.substring(8, rawBoomerangScript.length - 9)

    const rawListenerScript = scriptTags.listenerScripts

    const listenerScript = rawListenerScript.substring(8, rawListenerScript.length - 9)

    const rawCathodeScript = scriptTags.cathodeScript

    const cathodeScript = rawCathodeScript.substring(8, rawCathodeScript.length - 9)

    const root = createRoot(document.querySelector('#app')!)
    root.render(
        <CacheBuster currentVersion={appVersion} isEnabled={isProduction} isVerboseMode={false}>
            <AppBootstrap
                apiClient={apiClient}
                userAlias={userAlias}
                egretUrl={egretUrl}
                stage={stage}
                initialBusinessEntity={userBusinessEntity}
                userRolesPromise={userRolesPromise}
                isAdminPromise={isAdminPromise}
            />
        </CacheBuster>,
    )

    window.eval(boomerangScript)
    window.eval(listenerScript)
    window.eval(cathodeScript)
})()
